/**
 * The re-import in the interface (decision 64; part 23b, tables V′ and Y): Re-import DOCX… beside Import… with its file
 * picker, the re-import's status under the toolbar as the import's is, and the review dialog: each chapter of the file
 * with what taking it changes and a check box to take it, the book's chapters the file doesn't hold, the new pictures,
 * the Word comments Apply keeps (part 23c, V″), what to check, Apply and Cancel. What the status says is announced once
 * per phase (A13).
 */
import { createSignal } from 'solid-js';
import { DOCX_ACCEPT } from '../import/kind.ts';
import type { ReimportFailure, ReimportState } from '../reimport/controller.ts';
import type { Review, ReviewChapter } from '../reimport/review.ts';
import { focusOn } from './announce.ts';
import { importing } from './bundle-import.ts';
import { each, el, when } from '../ui/dom.ts';
import { ModalActions, ModalBody, ModalFoot } from './modal.ts';
import type { Child, On } from '../ui/dom.ts';
import { failureText, flagText } from './import.ts';
import Button from '../ui/Button.ts';
import Card from '../ui/Card.ts';
import Checkbox from '../ui/Checkbox.ts';
import Icon from '../ui/Icon.ts';
import { component } from '../ui/solid.ts';
import type { MenuItem } from '../ui/Menu.ts';
import type { Shell } from './shell.ts';

/** V′4, CS11, CS12: why a re-import wrote nothing, in words. */
export function reimportFailureText(shell: Pick<Shell, 't'>, reason: ReimportFailure): string {
  const { t } = shell;
  switch (reason) {
    case 'not-supported':
      return t('reimport.notSupported');
    case 'unreadable':
      return t('reimport.unreadable');
    case 'merge-failed':
      return t('reimport.mergeFailed');
    case 'book-changed':
      return t('reimport.bookChanged');
    case 'changed':
      return t('reimport.changed');
    case 'not-editable':
      return t('reimport.notEditable');
    case 'save-failed':
      return t('reimport.saveFailed');
    case 'stale':
      return t('reimport.stale');
    case 'write-failed':
      return t('reimport.writeFailed');
    case 'cancelled':
    case 'failed':
      return t('reimport.failed');
    // The file's own problems are said as an import's are (V′4).
    case 'too-large':
    case 'not-zip':
    case 'damaged':
    case 'unsupported':
    case 'bad-name':
    case 'too-many-files':
    case 'too-big':
    case 'not-docx':
    case 'empty':
    case 'expands':
    case 'own-export':
    case 'timed-out':
    case 'busy':
    case 'not-bundle':
    case 'newer':
    case 'file-changed':
      return failureText(shell, reason);
  }
}

/** A share in whole percent, 0 for nothing to share. */
function percent(done: number, total: number): number {
  return total === 0 ? 0 : Math.floor((100 * done) / total);
}

/** What the status says (V′-rows' outcomes); nothing while idle or under review. */
export function reimportText(shell: Pick<Shell, 't'>, state: ReimportState): string {
  const { t } = shell;
  switch (state.kind) {
    case 'idle':
    case 'review':
      return '';
    case 'reading':
      return t('reimport.reading', { name: state.fileName, percent: percent(state.done, state.total) });
    case 'comparing':
      return t('reimport.comparing', { done: state.done, total: state.total });
    case 'merging':
      return t('reimport.merging', { done: state.done, total: state.total });
    case 'adding':
      return t('reimport.adding', { done: state.done, total: state.total });
    case 'storing':
      return t('reimport.storing');
    case 'done':
      return [
        t('reimport.done', { count: state.changed }),
        ...(state.added > 0 ? [t('reimport.newChapters', { count: state.added })] : []),
        ...(state.copies > 0 ? [t('reimport.copies', { count: state.copies })] : []),
        ...(state.missing > 0 ? [t('reimport.missingPictures', { count: state.missing })] : []),
        // Part 23c, V″3: the comment records the write added.
        ...(state.comments > 0 ? [t('reimport.commentsKept', { count: state.comments })] : []),
      ].join(' ');
    case 'failed':
      return reimportFailureText(shell, state.reason);
  }
}

/** A13: what the re-import announces: its phases, never each percent, picture or chapter the status counts. */
export function reimportNews(shell: Pick<Shell, 't'>, state: ReimportState): string {
  switch (state.kind) {
    case 'reading':
      return shell.t('reimport.readingNews');
    case 'comparing':
      return shell.t('reimport.comparingNews');
    case 'merging':
      return shell.t('reimport.mergingNews');
    case 'adding':
      return shell.t('reimport.addingNews');
    case 'idle':
    case 'review':
    case 'storing':
    case 'done':
    case 'failed':
      return reimportText(shell, state);
  }
}

/** Whether a phase that Cancel stops is under way (V′3). */
function cancellable(state: ReimportState): boolean {
  return state.kind === 'reading' || state.kind === 'comparing' || state.kind === 'merging' || state.kind === 'adding';
}

/**
 * Y: Re-import DOCX… as a menu's item (the library's Menu), with its own file picker (hidden, beside the menu; the item
 * opens it), off while the book can't change or an import runs (V′2).
 */
export function ReimportItem(shell: Shell, look: Pick<MenuItem, 'label' | 'hint'>): { readonly item: MenuItem; readonly picker: Child } {
  const { workspace, reimporter } = shell;
  let picker: HTMLInputElement | undefined;
  return {
    item: {
      class: 'reimport-docx',
      label: look.label,
      hint: look.hint,
      get disabled() {
        return !workspace.writable() || workspace.book() === null || importing(shell);
      },
      onSelect: () => picker?.click(),
    },
    picker: el('input', {
      type: 'file',
      class: 'reimport-picker',
      accept: DOCX_ACCEPT,
      hidden: true,
      tabIndex: -1,
      'aria-hidden': 'true',
      ref: (input: HTMLInputElement) => {
        picker = input;
      },
      onChange: (event: On<Event, HTMLInputElement>) => {
        const file = event.currentTarget.files?.[0];
        // The same file chosen again must still fire a change.
        event.currentTarget.value = '';
        if (file === undefined) return;
        reimporter.pick(file);
        focusOn(shell, '.cancel-reimport');
      },
    }),
  };
}

/** V′3, V′4, V′8: the status region; Cancel and Dismiss leave the focus on Re-import DOCX… (A24). */
export function ReimportStatus(shell: Shell): Child {
  const { t, reimporter } = shell;
  const state = (): ReimportState => reimporter.state();
  return when(
    () => state().kind !== 'idle' && state().kind !== 'review',
    () =>
      el(
        'section',
        { class: 'export-status reimport-status', 'aria-label': t('reimport.label') },
        el('p', {}, () => reimportText(shell, state())),
        () => {
          const now = state();
          return cancellable(now) && 'done' in now ? el('progress', { max: Math.max(1, now.total), value: now.done }) : null;
        },
        when(
          () => cancellable(state()),
          () =>
            el(
              'button',
              {
                type: 'button',
                class: 'cancel-reimport',
                onClick: () => {
                  reimporter.cancel();
                  focusOn(shell, '.reimport-docx');
                },
              },
              t('reimport.cancel'),
            ),
        ),
        when(
          () => state().kind === 'done' || state().kind === 'failed',
          () =>
            el(
              'button',
              {
                type: 'button',
                class: 'dismiss-reimport',
                onClick: () => {
                  reimporter.dismiss();
                  focusOn(shell, '.reimport-docx');
                },
              },
              t('export.dismiss'),
            ),
        ),
      ),
  );
}

/** CS1–CS7: a chapter's state in words. */
function stateText(shell: Pick<Shell, 't'>, chapter: ReviewChapter): string {
  const { t } = shell;
  switch (chapter.state) {
    case 'unchanged':
      return t('reimport.state.unchanged');
    case 'here':
      return t('reimport.state.here');
    case 'changed':
      return chapter.base ? t('reimport.state.changed') : t('reimport.noBase');
    case 'both':
      return t('reimport.state.both');
    case 'new':
      return t('reimport.state.new');
  }
}

/** A take check box (the library's Checkbox): `id`, its label, and the signal it shows and sets. */
function TakeBox(id: string, label: string, taken: () => boolean, setTaken: (taken: boolean) => void): Child {
  return component(Checkbox, {
    id,
    class: 'reimport-take',
    label,
    get checked() {
      return taken();
    },
    onChange: (checked: boolean) => {
      setTaken(checked);
    },
  });
}

/**
 * One line of what taking a chapter changes: its icon and its word ("Removed:", "Added:") say which, never a colour or a
 * rule beside it alone (the brief's rule 6); long text wraps.
 */
function DiffLine(shell: Pick<Shell, 't'>, line: ReviewChapter['diff'][number]): Child {
  const { t } = shell;
  const removed = line.change === 'removed';
  return el(
    'li',
    { class: `diff-${line.change}` },
    component(Icon, { name: removed ? 'minus' : 'plus', size: 'sm' }),
    el('span', {}, el('strong', {}, removed ? t('reimport.removed') : t('reimport.addedText')), ' ', el('span', { class: 'diff-text' }, line.text === '' ? t('reimport.blank') : line.text)),
  );
}

/** V′5: one chapter of the review, a card: its title, state, words, take check boxes (text, file title) and what taking it changes. */
function ChapterView(shell: Shell, chapter: ReviewChapter, taken: () => boolean, setTaken: (taken: boolean) => void, titled: () => boolean, setTitled: (taken: boolean) => void): Child {
  const { t } = shell;
  const title = chapter.title === '' ? t('chapter.untitled') : chapter.title;
  const index = String(chapter.index);
  const body = [
    chapter.conflicts > 0 ? el('p', { class: 'reimport-conflicts' }, component(Icon, { name: 'warn', size: 'sm' }), el('span', {}, t('reimport.conflicts', { count: chapter.conflicts }))) : null,
    chapter.takeable ? TakeBox(`reimport-take-${index}`, t('reimport.take', { title }), taken, setTaken) : null,
    // CS8 (deep-fix r4): the file's title, its own choice, not taken unless the writer ticks it.
    chapter.rename === null ? null : TakeBox(`reimport-title-${index}`, t('reimport.rename', { title: chapter.rename }), titled, setTitled),
    chapter.diff.length > 0
      ? el(
          'div',
          { class: 'reimport-changes' },
          el('h5', {}, t('reimport.diff')),
          el('ul', { class: 'reimport-diff' }, each(() => chapter.diff, (line) => DiffLine(shell, line))),
          chapter.more > 0 ? el('p', {}, t('reimport.more', { count: chapter.more })) : null,
        )
      : null,
  ].filter((part) => part !== null);
  return el(
    'li',
    { class: `reimport-chapter state-${chapter.state}` },
    component(Card, {
      padding: 'sm',
      titleAs: 'h4',
      title,
      meta: el('p', {}, stateText(shell, chapter), ' ', t('reimport.words', { count: chapter.words })),
      children: body.length > 0 ? el('div', { class: 'reimport-chapter-body' }, body) : undefined,
    }),
  );
}

/** V′5–V′8: the review dialog's body. */
export function ReimportReview(shell: Shell, review: Review, pictures: number): Child {
  const { t, workspace, reimporter, ui } = shell;
  // CS15: a chapter that changes something is taken unless the writer says not.
  const [take, setTake] = createSignal<readonly boolean[]>(review.chapters.map((chapter) => chapter.takeable));
  // CS8′ (fix-next 27): a rename only the file made starts ticked; any other starts unticked.
  const [titles, setTitles] = createSignal<readonly boolean[]>(review.chapters.map((chapter) => chapter.renameTaken));
  // V″2 (part 23c): a comment in a chapter of the book is kept by Apply too.
  const anything = (): boolean =>
    review.comments.total > review.comments.inNew || review.chapters.some((chapter) => (chapter.takeable && take()[chapter.index] === true) || (chapter.rename !== null && titles()[chapter.index] === true));
  return [
    ModalBody(
      { class: 'reimport-review' },
      el('p', {}, t('reimport.summary', { count: review.chapters.length })),
      el('p', {}, t('reimport.intoBook')),
      el('p', {}, t('reimport.notRead')),
      el('h3', {}, t('reimport.chapters')),
      el(
        'ol',
        { class: 'reimport-chapters' },
        each(
          () => review.chapters,
          (chapter) =>
            ChapterView(
              shell,
              chapter,
              () => take()[chapter.index] === true,
              (taken) => {
                setTake((now) => now.map((was, i) => (i === chapter.index ? taken : was)));
              },
              () => titles()[chapter.index] === true,
              (taken) => {
                setTitles((now) => now.map((was, i) => (i === chapter.index ? taken : was)));
              },
            ),
        ),
      ),
      review.missing.length > 0
        ? [
            el('h3', {}, t('reimport.missingHeading')),
            el('p', {}, t('reimport.missingBody')),
            el('ul', { class: 'reimport-missing' }, each(() => review.missing, (chapter) => el('li', {}, chapter.title === '' ? t('chapter.untitled') : chapter.title))),
          ]
        : null,
      pictures > 0 ? el('p', {}, t('reimport.pictures', { count: pictures })) : null,
      // V″1 (part 23c): the comments Apply keeps.
      review.comments.total > 0
        ? el('p', {}, [t('reimport.comments', { count: review.comments.total }), ...(review.comments.inNew > 0 ? [t('reimport.commentsInNew', { count: review.comments.inNew })] : [])].join(' '))
        : null,
      review.flags.length > 0
        ? [
            el('h3', {}, t('reimport.check')),
            el('ul', { class: 'reimport-flags' }, each(() => review.flags, (flag) => el('li', {}, component(Icon, { name: 'warn', size: 'sm' }), el('span', {}, flagText(shell, flag, review.chapters.length))))),
          ]
        : null,
    ),
    ModalFoot(
      {},
      ModalActions(
        component(Button, {
          onClick: () => {
            reimporter.closeReview();
            ui.setDialog(null);
          },
          children: t('reimport.cancel'),
        }),
        component(Button, {
          variant: 'primary',
          class: 'confirm-reimport',
          get disabled() {
            return !workspace.writable() || !anything();
          },
          onClick: () => {
            // Applied before the dialog closes: its close (V′6) puts away only a review still waiting.
            const stored = reimporter.confirm(take(), titles());
            ui.setDialog(null);
            // A24: the status's Cancel while pictures are added; then the text, or the failure's Dismiss.
            focusOn(shell, '.cancel-reimport');
            void stored.then(() => {
              if (reimporter.state().kind === 'done') shell.focusEditor();
              else focusOn(shell, '.dismiss-reimport');
            });
          },
          children: t('reimport.apply'),
        }),
      ),
    ),
  ];
}
